import * as React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Box, flameTheme, Flex } from '@lightspeed/flame/Core';

import { UserPreferencesContext } from 'root/apps/contexts/UserPreferencesContext';
import { StatusDataContext } from 'root/apps/InventoryCount/contexts/StatusData';
import ExpandedToggle from 'root/apps/InventoryCount/Views/components/ExpandedToggle';

import ImportFile from './components/ImportFile';
import ImportOverview from './components/ImportOverview';
import ImportProgress from './components/ImportProgress';
import ImportTemplate from './components/ImportTemplate';
import MapColumn from './components/MapColumn';
import OfflineModal from './components/OfflineModal';
import Stepper from './components/Stepper';
import ValidationView from './components/ValidationView';

const DISPLAY_PREF_NAME = 'inventory_count_import_display_state';
const DISPLAY_STATE_SHOW = 'show';
const DISPLAY_STATE_HIDE = 'hide';

const ImportItemView: React.FC = () => {
  const { isLoading, data, getStatusData } = React.useContext(StatusDataContext);
  const { getUserPreferenceValueByName, updateUserPreferenceValueByName } =
    React.useContext(UserPreferencesContext);

  const displayState = getUserPreferenceValueByName(DISPLAY_PREF_NAME) || DISPLAY_STATE_SHOW;
  const [isExpanded, setIsExpanded] = React.useState(displayState === DISPLAY_STATE_SHOW);

  const handleClickOpen = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsExpanded(true);
    updateUserPreferenceValueByName(DISPLAY_PREF_NAME, DISPLAY_STATE_SHOW);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    updateUserPreferenceValueByName(
      DISPLAY_PREF_NAME,
      !isExpanded ? DISPLAY_STATE_SHOW : DISPLAY_STATE_HIDE
    );
  };

  React.useEffect(() => {
    getStatusData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // To disable import button if import is currently running
    const targetCountButton = document.getElementById(
      'inventory_count_item_button'
    ) as HTMLButtonElement;

    const targetCountInputField = document.getElementById(
      'inventory_count_item_input_field'
    ) as HTMLInputElement;

    if (
      data?.status === 'import_running' ||
      data?.status === 'awaiting_import' ||
      data?.status === 'import_queued'
    ) {
      targetCountInputField?.setAttribute('disabled', '');
      targetCountButton?.setAttribute('disabled', '');
    } else {
      targetCountInputField?.removeAttribute('disabled');
      targetCountButton?.removeAttribute('disabled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const uploadView = ['awaiting_upload', 'validation_failed', 'import_success'];
  const mapColumnsView = ['awaiting_input'];
  const validationView = ['awaiting_validation', 'validation_queued', 'validation_running'];
  const importOverviewView = ['ready_to_import', 'import_failed'];
  const importProgressView = ['awaiting_import', 'import_queued', 'import_running'];

  const statusCheck = (currentStatus: string | undefined, list: string[]) => {
    if (currentStatus) {
      const isStatusAvailable = list.includes(currentStatus);
      return isStatusAvailable;
    }
    return false;
  };

  const expanded = (
    <Box data-test="expanded-view">
      <ImportTemplate />
      {isLoading && !data ? (
        <Box p={2}>
          <Flex flexDirection="column">
            <Box pb={2}>
              <Bone height={4} width={150} />
            </Box>
            <Bone height={300} />
          </Flex>
        </Box>
      ) : null}
      {statusCheck(data?.status, uploadView) ? <ImportFile /> : null}
      {statusCheck(data?.status, mapColumnsView) ? <MapColumn /> : null}
      {statusCheck(data?.status, validationView) ? <ValidationView /> : null}
      {statusCheck(data?.status, importOverviewView) ? <ImportOverview /> : null}
      {statusCheck(data?.status, importProgressView) ? <ImportProgress /> : null}
      <Stepper />
    </Box>
  );

  const collapsed = (
    <Box data-test="collapsed-view" p={2}>
      <a
        href="#"
        onClick={handleClickOpen}
        data-test="expanded-open-link"
        style={{ color: flameTheme.colors.textBody }}
      >
        Open to bulk import items with a CSV or an XLSX file.
      </a>
    </Box>
  );

  return (
    <Box>
      <Flex flexDirection="column" justifyContent="space-between" height="100%">
        <OfflineModal />
        <ExpandedToggle isExpanded={isExpanded} onClick={toggleExpanded} />
        {isExpanded ? expanded : collapsed}
      </Flex>
    </Box>
  );
};

export default ImportItemView;
